import Mustache from 'mustache';
import './dot-form.scss';

type DotFormSettings = {
  maxTyres: number;
  ajaxEndpoint: string;
  loc_choose: string;
  tyres: string[];
  weeks: string[];
  years: string[];
  factoryCodes: string[];
}


declare global {
  interface Window {
    dot_form_config: DotFormSettings;
    dot_form_template: string;
  }
}

class DotForm {
  settings: DotFormSettings;
  tyresCount: number = 0; // track max tyres
  tyresIndex: number =  1; // unique index for html elements
  tyreData: object = {};
  #dom!: {
    root: HTMLElement;
    form: HTMLFormElement;
    addBtn: HTMLButtonElement;
    tyreDataInput: HTMLInputElement;
    section: HTMLDivElement;
  }

  constructor(element: HTMLElement, config: DotFormSettings) {
    this.settings = config;

    this.#dom = {
      root: element,
      form: element.closest('form') as HTMLFormElement,
      addBtn: element.querySelector('[data-dot-form-add-button]') as HTMLButtonElement,
      tyreDataInput: element.querySelector('input[name="tyre_data"]') as HTMLInputElement,
      section: element.querySelector('[data-dot-form-section]') as HTMLDivElement,
    };

    if(Object.keys(this.#dom).some(key => !this.#dom[key])) {
      console.error('[dot-form] missing element in dom', this.#dom);
      return;
    }

    this.attachEventListeners();

    // Init existing tyres if submitted -> we have data in tyre_data hidden element
    const existingData = this.#dom.tyreDataInput?.value;
    if (existingData) {
      const parsedData = JSON.parse(existingData);
      this.initTyres(parsedData);
      this.tyreData = parsedData;
    } else {
      this.renderNewTyre(this.tyresIndex);
    }
  }

  attachEventListeners() {
    // Add new tyre
    this.#dom.addBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.tyresIndex++;
      this.renderNewTyre(this.tyresIndex);
    });

    // Remove tyre
    this.#dom.root.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).matches('[data-dot-form-remove-button]')) {
        e.preventDefault();
        const target = (e.target as HTMLElement)?.dataset?.target || '';
        document.getElementById(target)?.remove();
        this.tyresCount--;
        delete this.tyreData[target];
        if (this.tyresCount < this.settings.maxTyres) {
          this.#dom.addBtn.removeAttribute('disabled');
        }
      }
    });

    // Country change
    this.#dom.form.querySelector('select#country')?.addEventListener('change', (e) => {
      const target = e.target as HTMLSelectElement;
      const countryISO = target?.selectedOptions[0].dataset.countryCodeIso || '';
      const region = target?.selectedOptions[0].dataset.regionCode || '';
      const countryName = target?.selectedOptions[0].dataset.countryName || '';
      const countryISOEl = this.#dom.form.querySelector('input[type="hidden"][name="country_iso"]') as HTMLInputElement;
      const regionEl = this.#dom.form.querySelector('input[type="hidden"][name="region"]') as HTMLInputElement;
      const countryNameEl = this.#dom.form.querySelector('input[type="hidden"][name="country_name"]') as HTMLInputElement;

      if (countryISOEl) countryISOEl.value = countryISO;
      if (regionEl) regionEl.value = region;
      if (countryNameEl) countryNameEl.value = countryName;
    });

    // Any of the tyre select fields change
    this.#dom.root.addEventListener('change', (e) => {
      if((e.target as HTMLSelectElement).matches('select')) {
        const select = e.target as HTMLSelectElement;
        const value = select.value;
        const field = select.name;
        const target = select.dataset.target;

        if(value) {
          select.classList.remove('error');
        }

        if(target) {
          this.tyreData[target][field] = value;
        }

        // Fetch tyre sizes after model is selected
        if(select.matches('.dot-model-select')) {
          const model = value;
          const sizeTarget = select.dataset.selectTarget;
          const dataTarget = select.dataset.target;
          if(sizeTarget && dataTarget) {
            this.tyreData[dataTarget].size = null;
            this.getTyreSizes(model, sizeTarget);
          }
        }
      }
    });

    // Validate form before submit
    this.#dom.form.addEventListener('submit', (e) => {
      if (!this.validateTyres()) {
        e.preventDefault();
        console.warn('[dot-form] validation failed');
      } else {
        e.preventDefault();
        let formattedTyreData = {};
        // populate hidden tyre_data-field for Stage
        let tyres = Object.values(this.tyreData);
        for (let i = 0; i < tyres.length; i++) {
          formattedTyreData[`tyre_${i + 1}`] = {...tyres[i]};
          formattedTyreData[`tyre_${i + 1}`].dot = tyres[i].fc + tyres[i].size.split('T')[1] + tyres[i].wom + tyres[i].yom;
        }

        this.#dom.tyreDataInput.value = JSON.stringify(formattedTyreData);
        this.#dom.form.submit();
      }
    })

  }

  /**
   * Get tyre sizes after model has been selected
   */
  getTyreSizes(model: string, targetElId: string) {
    const url = this.settings.ajaxEndpoint + model;
    const request = new XMLHttpRequest();
    request.open('GET', url, false); // `false` makes the request synchronous. We must have this data before moving on.
    request.send(null);

    if (request.status === 200) {
      try {
        const data = JSON.parse(request.responseText);
        const sorted = Object.keys(data).sort((a, b) => parseFloat(a.replace('"','')) - parseFloat(b.replace('"','')));
        let sizeOptions = '<option selected="selected" disabled="disabled">' + this.settings.loc_choose +'</option>';
        sorted.forEach((prop) => {
          if (Object.prototype.hasOwnProperty.call(data, prop)) {
            sizeOptions += '<option disabled="disabled">'+prop+'</option>';
            for(let i=0; i<data[prop].length; i++) {
              sizeOptions += '<option value="'+data[prop][i].tid+'">'+data[prop][i].info+'</option>';
            }
          }
        });

        const targetEl = document.getElementById(targetElId) as HTMLSelectElement;
        targetEl.innerHTML = sizeOptions;
        targetEl.disabled = false;
      } catch (error) {
        console.error('[dot-form] Error parsing response to JSON in getTyreSizes', error);
      }
    }
  }

  validateTyres(): boolean {
    const selects = this.#dom.root.querySelectorAll('select');
    let valid = true;
    let firstEl:HTMLSelectElement | null = null;
    for (const select of selects) {
      if (!select.value || select.value === this.settings.loc_choose) {
        valid = false;
        select.classList.add('error');
        if (!firstEl) {
          firstEl = select;
        }
      }
    }
    if (!valid) {
      if(firstEl) {
        firstEl.scrollIntoView({behavior: 'smooth'});
      }
    }
    return valid;
  }

  // Init tyres from existing data
  // Create html elements, populate with data
  // eg. after submitting but some fields were missing data -> stage renders empty fields
  initTyres(existingData) {
    for (const tyreId of Object.keys(existingData)) {
      const tyre = existingData[tyreId];
      this.renderNewTyre(this.tyresIndex, tyre);
      this.tyresIndex++;

      const tyreEl = this.#dom.root.querySelector('#' + tyreId);
      if(tyreEl) {
        (tyreEl.querySelector('select[name="model') as HTMLSelectElement).value = tyre.model;
        this.getTyreSizes(tyre.model, tyreId + '_size');
        (tyreEl.querySelector('select[name="size') as HTMLSelectElement).value = tyre.size;
        (tyreEl.querySelector('select[name="qty') as HTMLSelectElement).value = tyre.qty;
        (tyreEl.querySelector('select[name="fc') as HTMLSelectElement).value = tyre.fc;
        (tyreEl.querySelector('select[name="wom') as HTMLSelectElement).value = tyre.wom;
        (tyreEl.querySelector('select[name="yom') as HTMLSelectElement).value = tyre.yom;
      }
    }
  }

  renderNewTyre(index, existingData?: object ) {
    this.tyresCount++;
    if (this.tyresCount === this.settings.maxTyres) {
      this.#dom.addBtn.disabled = true;
    }
    const newId = `tyre_${index}`;

    const newTyreData = {
      id: newId,
      tyres: this.settings.tyres,
      weeks: this.settings.weeks,
      years: this.settings.years,
      factoryCodes: this.settings.factoryCodes,
      showRemove: (this.tyresCount > 1 ? true : false),
    }
    const tyreHtml = Mustache.render(
      window.dot_form_template,
      newTyreData
    );

    this.#dom.section.insertAdjacentHTML('beforeend', tyreHtml);

    // add to tyreData object
    this.tyreData[newId] = existingData ? {...existingData} : {};

    // init model and size with previous selection if they exist
    if (this.tyresCount > 1) {
      let prevTyre;
      let prevIndex = this.tyresIndex;
      while (!prevTyre && prevIndex >= 1) {
        prevIndex--;
        if (this.tyreData[`tyre_${prevIndex}`]) {
          prevTyre = this.tyreData[`tyre_${prevIndex}`];
        }
      }
      if (prevTyre) {
        if (prevTyre.model) {
          const curElement = this.#dom.section.querySelector('#' + newId + '_model') as HTMLSelectElement;
          if(curElement) {
            curElement.value = prevTyre.model;
          }
          this.tyreData[newId].model = prevTyre.model;
        }

        const prevSize = this.#dom.root.querySelector(`#tyre_${prevIndex}_size`) as HTMLSelectElement;
        if (prevSize?.value && prevSize?.options) {
          const curElement = this.#dom.section.querySelector('#' + newId + '_size') as HTMLSelectElement;

          if(curElement) {
            for (const option of prevSize.options) {
              curElement.append(new Option(option.text, option.value));
            }
            curElement.value = prevSize.value;
            curElement.disabled = false;
          }

          this.tyreData[newId].size = prevSize.value;
        }
      }
    }
  }
}

export function init(element: HTMLElement) {
  console.log('dot-form.init()');

  if(element) {
    if (window.dot_form_config) {
      new DotForm(element, window.dot_form_config);
    } else {
      console.error('[dot-form] init failed, no window.dot_form_config');
    }
  }
}

export default DotForm;
